import * as ngCore from '@angular/core'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import { ComponentStore } from '@ngrx/component-store'

import * as models from '../models'

import * as plugin from './ec-plugin'

const DEFAULT_CONFIG = {
  overlay: {
    maxHeight: 260,
  },
}

interface AtnDsSelectSingleEcState<
  TTypeParams extends ec.models.TypeParams.Constraint,
> {
  /** @see {@link ec.Collection} */
  ec?: ec.Collection<TTypeParams>
  /**
   * Label above select to be displayed
   *
   * @default undefined
   */
  label?: string
  /**
   * Is component disabled
   *
   * @default false
   */
  disabled: boolean
  /**
   * @default  DEFAULT_CONFIG
   * @see {@link models.Config}
   */
  config: models.Config
  /**
   * "Source" of the select
   *
   * @default plugin.Plugin.id
   */
  descriptor: string
  /** @see {@link ec.models.Target} */
  selectionTarget?: ec.models.Target
  /**
   * Extra {@link models.Item} to place _before_ the entities
   */
  prepend?: readonly models.Item[]
  /**
   * Extra {@link models.Item} to place _after_ the entities
   */
  append?: readonly models.Item[]
  /**
   * If given, this group is used instead of the current list
   */
  groupId?: ec.plugins.standard.group.models.Group.Id
  /**
   * Whether the select is required or not.
   *
   * @default false
   */
  required?: boolean
}

/** TODO: explain */
@ngCore.Injectable()
export class AtnDsSelectSingleEcStore<
  TTypeParams extends ec.models.TypeParams.Constraint,
> extends ComponentStore<AtnDsSelectSingleEcState<TTypeParams>> {
  /** @see {@link AtnDsSelectSingleEcState.ec} */
  public readonly ecSg = this.selectSignal(state => state.ec)
  /** @see {@link plugin.Plugin} */
  public readonly pluginSg = ngCore.computed((_ec = this.ecSg()) =>
    _ec?.plugins.lookup(plugin.Plugin<TTypeParams>).get(),
  )
  /** @see {@link plugin.models.Config} */
  public readonly pluginConfigSg = ngCore.computed(
    (_plugin = this.pluginSg()) => _plugin?.config,
  )
  /** @see {@link AtnDsSelectSingleEcState.config} */
  public readonly componentConfigSg = this.selectSignal(state => state.config)
  /** @see {@link AtnDsSelectSingleEcState.descriptor} */
  public readonly descriptorSg = this.selectSignal(state => state.descriptor)
  /** @see {@link AtnDsSelectSingleEcState.label} */
  public readonly labelSg = this.selectSignal(state => state.label)
  /** @see {@link AtnDsSelectSingleEcState.disabled} */
  public readonly disabledSg = this.selectSignal(state => state.disabled)
  /** @see {@link AtnDsSelectSingleEcState.config} */
  public readonly configSg = this.selectSignal(state => state.config)
  /** @see {@link  AtnDsSelectSingleEcState.selectionTarget} */
  public readonly selectionTargetSg = this.selectSignal(
    state => state.selectionTarget,
  )
  /** @see {@link  AtnDsSelectSingleEcState.prepend} */
  public readonly prependSg = this.selectSignal(state => state.prepend)
  /** @see {@link  AtnDsSelectSingleEcState.append} */
  public readonly appendSg = this.selectSignal(state => state.append)
  /** @see {@link  AtnDsSelectSingleEcState.groupId} */
  public readonly groupIdSg = this.selectSignal(state => state.groupId)

  /** @see {@link AtnDsSelectSingleEcState.required} */
  public readonly requiredSg = this.selectSignal(
    state => state.required ?? false,
  )
  /**
   * Error message to display.
   */
  public readonly errorSg = ngCore.computed((_ec = this.ecSg()) =>
    _ec?.plugins.currentListPlugin.feature.selectors.error.asSignal(),
  )

  /**
   * The {@link ec.models.EntityId ID} of the selected entity.
   *
   * If {@link selectionTargetSg} is provided, it will be used to determine
   * the selected entity ID.
   */
  public readonly selectedIdSg = ngCore.computed(
    (_plugin = this.pluginSg(), target = this.selectionTargetSg()) =>
      _plugin?.collection.plugins.selectionPlugin.feature.selectors
        .getFirstSelectedId({ target })
        .asSignal(),
  )

  /**
   * {@link models.Item Items} to display in the select.
   */
  public readonly itemsSg = ngCore.computed(
    (
      _plugin = this.pluginSg(),
      // TODO: The selection target is used specifically for creating a new custom
      // label. There is a tightly coupled relationship between 'items' and the
      // handling of 'custom click'. This might need to be refactored in the
      // future, and this code will need to be updated accordingly.
      selectionTarget = this.selectionTargetSg(),
      groupId = this.groupIdSg(),
      prepend = this.prependSg(),
      append = this.appendSg(),
    ) =>
      // TODO: think about what should happen if selection target is for some
      // reason undefined, for now, we will return empty array.
      selectionTarget && _plugin
        ? _plugin.feature.selectors
            .getAvailableItems({ append, groupId, prepend, selectionTarget })
            .asSignal()
        : [],
  )

  /**
   * Indicates if {@link itemsSg} are loading.
   *
   */
  public readonly itemsLoadingSg = ngCore.computed(
    (_ec = this.ecSg()) =>
      _ec?.plugins.currentListPlugin.feature.selectors.isLoading.asSignal() ??
      false,
  )

  /* ------------------------ Edit Custom Label Dialog ------------------------ */

  /** @see {@link plugin.State.editingCustomLabel} */
  private readonly _editingCustomLabelSg = ngCore.computed(
    (_plugin = this.pluginSg()) =>
      _plugin?.feature.selectors.editingCustomLabel.asSignal(),
  )
  /** Indicates whether the custom label dialog should be shown. */
  public readonly presentEditCustomLabelDialogSg = ngCore.computed(
    (editingCustomLabel = this._editingCustomLabelSg()) => !!editingCustomLabel,
  )
  /** @see {@link _editingCustomLabelSg } */
  public readonly currentCustomLabelSg = ngCore.computed(
    (editingCustomLabelSg = this._editingCustomLabelSg()) =>
      // If currentCustomEntity or pluginConfig is not defined, return undefined
      editingCustomLabelSg?.label,
  )
  /** @see {@link _editingCustomLabelSg } */
  public readonly currentCustomSelectionTargetSg = ngCore.computed(
    (editingCustomLabelSg = this._editingCustomLabelSg()) =>
      editingCustomLabelSg?.selectionTarget,
  )

  constructor() {
    super({
      config: DEFAULT_CONFIG,
      descriptor: plugin.Plugin.id,
      disabled: false,
      required: false,
    })
  }

  /**
   * Handle the change event of the select
   */
  public selectChangeHandler(value: unknown) {
    const _plugin = this.pluginSg()
    const selectionTarget = this.selectionTargetSg()
    const source = this.descriptorSg()

    _plugin?.collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from(source)
      .dispatch({
        key: selectionTarget,
        // FIXME: Avoid type casting. Define a proper type for value
        value: value ? { set: [value as string] } : { release: true },
      })
  }

  /** TODO:  */
  public submitCustomLabel() {
    const _plugin = this.pluginSg()
    _plugin?.feature.actions.submitCustomLabel
      .from(
        _getCustomDialogDescriptor(
          this.descriptorSg(),
          'Footer.Buttons.Submit.Clicked',
        ),
      )
      .dispatch()
  }
  /** TODO:  */
  public cancelCustomEdit() {
    const _plugin = this.pluginSg()
    _plugin?.feature.actions.discardCustomLabel
      .from(
        _getCustomDialogDescriptor(
          this.descriptorSg(),
          'Footer.Buttons.Cancel.Clicked',
        ),
      )
      .dispatch()
  }
  /** TODO:  */
  public closeCustomEdit() {
    const _plugin = this.pluginSg()
    _plugin?.feature.actions.discardCustomLabel
      .from(
        _getCustomDialogDescriptor(
          this.descriptorSg(),
          'Header.Buttons.Close.Clicked',
        ),
      )
      .dispatch()
  }

  /** TODO:  */
  public editCustomLabel(label: string) {
    const _plugin = this.pluginSg()
    const selectionTarget = this.selectionTargetSg()
    if (selectionTarget && _plugin) {
      _plugin?.feature.actions.editCustomLabel
        .from(_getCustomDialogDescriptor(this.descriptorSg(), 'Label.Changed'))
        .dispatch({ label, selectionTarget })
    } else {
      console.warn('No selection target or plugin found')
    }
  }

  public errorHandler(): void {
    const collection = this.ecSg()

    collection?.plugins.currentListPlugin.feature.actions.load
      .from(`${this.descriptorSg()}.ErrorBanner.Buttons.Fetch.Clicked`)
      .dispatch()
  }
}

function _getCustomDialogDescriptor(
  controlDescriptor: string,
  source: string,
): string {
  return `${controlDescriptor}.AddCustomLabelDialog.${source}`
}
